/**
 * Recognise YouTube / Vimeo links pasted by organisers and turn them into
 * embeddable player URLs. Anything else is left alone (opened as a plain link).
 */

export type ParsedVideo =
  | { provider: 'youtube'; id: string }
  | { provider: 'vimeo'; id: string; hash?: string };

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

export function parseVideoUrl(raw: string | null | undefined): ParsedVideo | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\.|^m\./, '');

  if (host === 'youtu.be') {
    const id = url.pathname.split('/')[1] ?? '';
    return YOUTUBE_ID.test(id) ? { provider: 'youtube', id } : null;
  }

  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const fromQuery = url.searchParams.get('v');
    if (fromQuery && YOUTUBE_ID.test(fromQuery)) return { provider: 'youtube', id: fromQuery };
    const m = url.pathname.match(/^\/(?:embed|live|shorts|v)\/([A-Za-z0-9_-]{11})(?:\/|$)/);
    return m ? { provider: 'youtube', id: m[1] } : null;
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    // vimeo.com/<id>, vimeo.com/<id>/<hash>, player.vimeo.com/video/<id>?h=<hash>
    const m = url.pathname.match(/^\/(?:video\/)?(\d+)(?:\/([A-Za-z0-9]+))?\/?$/);
    if (!m) return null;
    const hash = m[2] ?? url.searchParams.get('h') ?? undefined;
    return { provider: 'vimeo', id: m[1], hash };
  }

  return null;
}

/** Player URL for the full-screen recording page: autoplay on, no loop. */
export function buildEmbedUrl(video: ParsedVideo): string {
  if (video.provider === 'youtube') {
    const params = new URLSearchParams({ autoplay: '1', rel: '0', playsinline: '1' });
    return `https://www.youtube-nocookie.com/embed/${video.id}?${params}`;
  }
  const params = new URLSearchParams({ autoplay: '1', autopause: '0', badge: '0' });
  if (video.hash) params.set('h', video.hash);
  return `https://player.vimeo.com/video/${video.id}?${params}`;
}

export function recordingPath(eventId: string, materialId: string): string {
  return `/event/${eventId}/recording/${materialId}`;
}

/** No-login CONFY watch page for a material whose organizer switched on a public link. */
export function publicRecordingPath(token: string): string {
  return `/shared/recording/${token}`;
}
